/** Small helpers shared by the vault screens. */
import type { ChainInfo, NetworkMode } from "@custos/api-client";
import type { KeyFamily } from "@custos/vault";
import { useEffect } from "react";
import { FAMILY_KEY } from "./chains";
import { useConfig } from "./queries";
import { useVault } from "./vault";

/** The vault state, loaded for the server's network mode. */
export function useVaultSession() {
  const mode = useConfig().data?.networkMode;
  const vault = useVault();
  useEffect(() => {
    if (mode) void useVault.getState().load(mode);
  }, [mode]);
  return vault;
}

export const FAMILY_NAME: Record<KeyFamily, string> = {
  EVM: "EVM",
  TRON: "Tron",
  BITCOIN: "Bitcoin",
  SOLANA: "Solana",
};

/** "Ethereum, Base, Arbitrum One, BNB Smart Chain" — the chains one family's address works on. */
export function familyChains(chains: ChainInfo[], family: KeyFamily): string {
  return chains
    .filter((c) => FAMILY_KEY[c.family] === family)
    .map((c) => c.displayName)
    .join(", ");
}

/** Where to find the key in other wallets, and what it looks like. */
export function privateKeyHint(family: KeyFamily, mode: NetworkMode): string {
  switch (family) {
    case "EVM":
      return "64 characters (0-9, a-f), with or without 0x. MetaMask: Account details → Show private key.";
    case "TRON":
      return "64 characters (0-9, a-f). TronLink: Export account → Private key.";
    case "BITCOIN":
      return mode === "testnet"
        ? "WIF format, starts with c (testnet). Electrum/Sparrow: native SegWit wallets only."
        : "WIF format, starts with K or L. Electrum/Sparrow: native SegWit (bc1…) wallets only.";
    case "SOLANA":
      return "Phantom / Solflare: Show private key (a long base58 text), or a solana-keygen [ … ] file.";
  }
}
